import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, DatePicker, InputNumber, Button, Card, Modal, Table, message, Spin, Space, Radio } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useMenuPermission } from '../hooks/useMenuPermission';

const { TextArea } = Input;

function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useMenuPermission();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [teams, setTeams] = useState([]);
  const [growthMethods, setGrowthMethods] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [user, setUser] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [fieldPermissions, setFieldPermissions] = useState({});

  // 查重相关
  const [dupVisible, setDupVisible] = useState(false);
  const [dupData, setDupData] = useState([]);
  const [dupCheckType, setDupCheckType] = useState('account');
  const [pendingValues, setPendingValues] = useState(null);
  const [pendingAccounts, setPendingAccounts] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) setUser(JSON.parse(stored));

    loadSuppliers();
    loadStatuses();
    loadTeams();
    loadGrowthMethods();
    loadPlatforms();

    if (id === 'new') {
      setIsNew(true);
      form.setFieldValue('order_time', dayjs());
    } else {
      loadOrder();
    }
    loadFieldPermissions();

    // 监听用户权限更新
    const handleUserUpdated = () => {
      const s = localStorage.getItem('user');
      if (s) {
        setUser(JSON.parse(s));
        loadFieldPermissions();
      }
    };
    window.addEventListener('userUpdated', handleUserUpdated);
    return () => window.removeEventListener('userUpdated', handleUserUpdated);
  }, [id]);

  const loadFieldPermissions = async () => {
    try {
      const stored = localStorage.getItem('user');
      const u = stored ? JSON.parse(stored) : null;
      const endpoint = u?.role === 'supplier' ? '/suppliers/my/fields' : '/users/my/fields';
      const res = await api.get(endpoint);
      if (res.code === 200) {
        const permMap = {};
        res.data.forEach(f => {
          permMap[f.field_name] = { visible: f.is_visible === true || f.is_visible === 1, editable: f.is_editable === true || f.is_editable === 1 };
        });
        setFieldPermissions(permMap);
      }
    } catch (err) {}
  };

  const loadOrder = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/orders/${id}`);
      if (res.code === 200) {
        const data = res.data;
        if (data.order_time) data.order_time = dayjs(data.order_time);
        if (data.complete_date) data.complete_date = dayjs(data.complete_date);
        form.setFieldsValue(data);
        setOrderData(data);
      }
    } catch (err) {}
    setLoading(false);
  };

  const loadSuppliers = async () => {
    try {
      const res = await api.get('/suppliers/all');
      if (res.code === 200) setSuppliers(res.data);
    } catch (err) {}
  };

  const loadStatuses = async () => {
    try {
      const res = await api.get('/statuses');
      if (res.code === 200) setStatuses(res.data);
    } catch (err) {}
  };

  const loadTeams = async () => {
    try {
      const res = await api.get('/teams');
      if (res.code === 200) setTeams(res.data);
    } catch (err) {}
  };

  const loadGrowthMethods = async () => {
    try {
      const res = await api.get('/growth-methods');
      if (res.code === 200) setGrowthMethods(res.data);
    } catch (err) {}
  };

  const loadPlatforms = async () => {
    try {
      const res = await api.get('/platforms');
      if (res.code === 200) setPlatforms(res.data);
    } catch (err) {}
  };

  // 涨粉方式变化时自动填入实际价格（参考价格由后端按主体解析，前端不处理）
  const handleGrowthMethodChange = (value) => {
    if (value) {
      const method = growthMethods.find(g => g.name === value);
      if (method) {
        // 涨粉方式：price=参考价格，reference_price=实际价格
        if (method.reference_price && !form.getFieldValue('actual_price')) {
          form.setFieldValue('actual_price', method.reference_price);
        }
      }
    }
  };

  // 执行实际创建
  const doCreate = async (values, accountNames) => {
    setSaving(true);
    const { account_name, ...commonFields } = values;
    let successCount = 0;
    let errorCount = 0;

    for (const account of accountNames) {
      try {
        const orderData = { ...commonFields, account_name: account };
        const res = await api.post('/orders', orderData);
        if (res.code === 200) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (err) {
        errorCount++;
      }
    }

    if (errorCount > 0) {
      message.warning(`成功 ${successCount} 条，失败 ${errorCount} 条`);
    } else {
      message.success(`成功创建 ${successCount} 条订单`);
    }
    setSaving(false);
    navigate('/orders');
  };

  // 查重
  const checkDuplicate = async (accounts, checkType) => {
    try {
      const res = await api.get('/orders/check-duplicate', {
        params: { accounts: accounts.join(','), check_type: checkType }
      });
      if (res.code === 200) return res.data;
    } catch (err) {}
    return [];
  };

  // 处理保存/创建
  const handleSave = async () => {
    try {
      setSaving(true);
      // 只获取已注册的表单字段值
      const values = form.getFieldsValue();

      // 转换日期格式
      if (values.order_time && typeof values.order_time === 'object' && values.order_time.format) {
        values.order_time = values.order_time.format('YYYY-MM-DD HH:mm:ss');
      }
      if (values.complete_date && typeof values.complete_date === 'object' && values.complete_date.format) {
        values.complete_date = values.complete_date.format('YYYY-MM-DD');
      }

      // 计算涨粉量
      if (values.initial_fans && values.final_fans) {
        values.growth_count = values.final_fans - values.initial_fans;
      }

      if (isNew) {
        const accountNames = (values.account_name || '')
          .split('\n')
          .map(s => s.trim())
          .filter(s => s);

        if (accountNames.length === 0) {
          message.error('请输入抖音号');
          setSaving(false);
          return;
        }

        // 查重
        const duplicates = await checkDuplicate(accountNames, dupCheckType);
        if (duplicates.length > 0) {
          setSaving(false);
          const dupAccounts = [...new Set(duplicates.map(d => d.account_name))];
          setDupData(duplicates);
          setPendingValues(values);
          setPendingAccounts(accountNames);
          setDupVisible(true);
          message.warning(`发现 ${dupAccounts.length} 个重复抖音号，请选择处理方式`);
          return;
        }

        await doCreate(values, accountNames);
        setSaving(false);
      } else {
        // 过滤掉无编辑权限的字段
        const editableValues = {};
        Object.keys(values).forEach(key => {
          if (isFieldEditable(key)) {
            editableValues[key] = values[key];
          }
        });

        if (Object.keys(editableValues).length === 0) {
          message.warning('没有可编辑的字段');
          setSaving(false);
          return;
        }

        // 修改抖音号：检测是否已存在，提示确认
        if (editableValues.account_name !== undefined && orderData) {
          const newName = String(editableValues.account_name || '').trim();
          const oldName = String(orderData.account_name || '').trim();
          if (newName && newName !== oldName) {
            const dupRes = await api.get('/orders/check-account', { params: { account: newName, exclude_id: id } });
            const dups = (dupRes.data && dupRes.data.duplicates) || [];
            if (dups.length > 0) {
              const ok = await new Promise(resolve => {
                Modal.confirm({
                  title: '抖音号已存在',
                  content: `抖音号 ${newName} 在系统中已存在 ${dups.length} 条订单，是否继续修改？`,
                  okText: '继续修改',
                  cancelText: '取消',
                  onOk: () => resolve(true),
                  onCancel: () => resolve(false)
                });
              });
              if (!ok) { setSaving(false); return; }
              editableValues.confirm_duplicate = true;
            }
          }
        }

        const res = await api.put(`/orders/${id}`, editableValues);
        setSaving(false);
        if (res.code === 200) {
          message.success('保存成功');
          navigate('/orders');
        } else {
          message.error(res.message || '保存失败');
        }
      }
    } catch (err) {
      setSaving(false);
      message.error('保存出错: ' + (err.message || String(err)));
    }
  };

  // 去重后创建
  const handleDedupCreate = async () => {
    const dupSet = new Set(dupData.map(d => d.account_name));
    const uniqueAccounts = pendingAccounts.filter(a => !dupSet.has(a));
    if (uniqueAccounts.length === 0) {
      message.warning('去除重复后没有可添加的抖音号');
      return;
    }
    setDupVisible(false);
    await doCreate(pendingValues, uniqueAccounts);
    setDupData([]);
    setPendingValues(null);
    setPendingAccounts([]);
  };

  // 强制创建（含重复）
  const handleForceCreate = async () => {
    setDupVisible(false);
    await doCreate(pendingValues, pendingAccounts);
    setDupData([]);
    setPendingValues(null);
    setPendingAccounts([]);
  };

  // 选项文案悬停显示完整内容
  const withTitle = (options) => options.map(o => ({ ...o, title: o.label }));

  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.username === 'admin';
  const isLocked = orderData?.status_is_locked === 1;

  // 数据权限限制修改的状态：订单处于该状态后即锁定，禁止再改出；可正常改入
  const restrictedStatusIds = (user?.data_permissions?.statuses || []).map(Number);
  const isStatusRestricted = !isSuperAdmin && orderData?.status_id != null &&
    restrictedStatusIds.includes(Number(orderData.status_id));
  // 可作为目标的可选状态：全部状态都可改入（含锁定/受限状态）；
  // 限制只作用于「改出」——订单一旦处于锁定或受限状态，就不能再改成其他状态
  const selectableStatuses = statuses;

  // 判断是否有新增/删除权限
  const canAdd = canEdit && (isSuperAdmin || user?.allow_add !== 0);
  const canDelete = canEdit && (isSuperAdmin || user?.allow_delete !== 0);

  // 判断字段是否可编辑（字段权限为唯一判断依据）
  const isFieldEditable = (fieldName) => {
    if (isSuperAdmin) return true;
    // 锁定状态 / 权限限制状态：订单整体只读，禁止改出
    if (isLocked || isStatusRestricted) return false;
    const perm = fieldPermissions[fieldName];
    if (isAdmin) {
      return !perm || perm.editable === true || perm.editable === 1;
    }
    if (isNew) return !perm || perm.editable === true || perm.editable === 1;
    // 主体：按字段权限
    return !perm || perm.editable === true || perm.editable === 1;
  };

  // 判断字段是否可见
  const isFieldVisible = (fieldName) => {
    if (isSuperAdmin) return true;
    const perm = fieldPermissions[fieldName];
    return !perm || perm.visible === true || perm.visible === 1;
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  // 通用的表单项样式
  const formItemStyle = { marginBottom: 12 };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>{isNew ? '新增订单' : `订单详情 #${id}`}</h3>
        <Space>
          {!isNew && isLocked && (
            <span style={{ color: '#ff4d4f', fontSize: 12 }}>[状态已锁定]</span>
          )}
          <Button onClick={() => navigate('/orders')}>返回列表</Button>
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        size="small"
      >
        {/* 抖音号输入 - 新增时大框批量输入 */}
        {isNew ? (
          <Card title="抖音号（一行一个，批量新增）" style={{ marginBottom: 12 }} size="small">
            <Form.Item name="account_name" rules={[{ required: true, message: '请输入抖音号' }]} style={formItemStyle}>
              <TextArea
                rows={6}
                placeholder={"请输入抖音号，一行一个\n例如：\n抖音号1\n抖音号2\n抖音号3"}
                onKeyDown={(e) => { if (e.key === 'Enter') e.stopPropagation(); }}
              />
            </Form.Item>
            <div style={{ marginBottom: 8 }}>
              <span style={{ marginRight: 12, fontSize: 13 }}>查重方式：</span>
              <Radio.Group size="small" value={dupCheckType} onChange={e => setDupCheckType(e.target.value)}>
                <Radio value="account">按账号查重</Radio>
                <Radio value="account_method">按账号+涨粉方式查重</Radio>
              </Radio.Group>
            </div>
            <div style={{ color: '#999', fontSize: 12 }}>提示：下方填写的字段会应用到所有新增的抖音号，提交时自动查重</div>
          </Card>
        ) : (
          <Card title="基本信息" style={{ marginBottom: 12 }} size="small">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {isFieldVisible('account_name') && <Form.Item name="account_name" label="抖音号" style={formItemStyle}>
                <Input disabled={!isFieldEditable('account_name')} />
              </Form.Item>}
              {isFieldVisible('platform') && <Form.Item name="platform" label="平台" style={formItemStyle}>
                <Select popupMatchSelectWidth={false} allowClear disabled={!isFieldEditable('platform')}
                  options={withTitle(platforms.map(p => ({ label: p.name, value: p.name })))}
                />
              </Form.Item>}
              {isFieldVisible('handler') && <Form.Item name="handler" label="受理员工" style={formItemStyle}>
                <Input disabled={!isFieldEditable('handler')} />
              </Form.Item>}
              {isFieldVisible('team') && <Form.Item name="team" label="小组" style={formItemStyle}>
                <Select popupMatchSelectWidth={false} allowClear showSearch placeholder="选择小组" disabled={!isFieldEditable('team')}
                  options={withTitle(teams.map(t => ({ label: t.name, value: t.name })))}
                />
              </Form.Item>}
            </div>
          </Card>
        )}

        {/* 基本信息 */}
        <Card title="基本信息" style={{ marginBottom: 12 }} size="small">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {isAdmin && isFieldVisible('supplier_id') && (
              <Form.Item name="supplier_id" label="主体" style={formItemStyle}>
                <Select popupMatchSelectWidth={false}
                  allowClear
                  placeholder="选择主体"
                  options={withTitle(suppliers.map(s => ({ label: s.name, value: s.id })))}
                />
              </Form.Item>
            )}
            {isNew && (
              <>
                {isFieldVisible('platform') && <Form.Item name="platform" label="平台" style={formItemStyle}>
                  <Select popupMatchSelectWidth={false} allowClear placeholder="选择平台" disabled={!isFieldEditable('platform')}
                    options={withTitle(platforms.map(p => ({ label: p.name, value: p.name })))}
                  />
                </Form.Item>}
                {isFieldVisible('handler') && <Form.Item name="handler" label="受理员工" style={formItemStyle}>
                  <Input placeholder="输入员工名" disabled={!isFieldEditable('handler')} />
                </Form.Item>}
                {isFieldVisible('team') && <Form.Item name="team" label="小组" rules={[{ required: true, message: '请选择小组' }]} style={formItemStyle}>
                  <Select popupMatchSelectWidth={false} allowClear showSearch placeholder="选择小组" disabled={!isFieldEditable('team')}
                    options={withTitle(teams.map(t => ({ label: t.name, value: t.name })))}
                  />
                </Form.Item>}
              </>
            )}
            {isFieldVisible('period') && <Form.Item name="period" label="期数" style={formItemStyle}>
              <Input placeholder="输入期数" disabled={!isFieldEditable('period')} />
            </Form.Item>}
            {isFieldVisible('group_name') && <Form.Item name="group_name" label="群名" style={formItemStyle}>
              <Input placeholder="输入群名" disabled={!isFieldEditable('group_name')} />
            </Form.Item>}
            {isFieldVisible('order_time') && <Form.Item name="order_time" label="下单时间" style={formItemStyle}>
              <DatePicker showTime style={{ width: '100%' }} disabled={!isFieldEditable('order_time')} />
            </Form.Item>}
            {isFieldVisible('contact_person') && <Form.Item name="contact_person" label="对接人" style={formItemStyle}>
              <Input placeholder="输入对接人" disabled={!isFieldEditable('contact_person')} />
            </Form.Item>}
            {isFieldVisible('growth_method') && <Form.Item name="growth_method" label="涨粉方式" style={formItemStyle}>
              <Select popupMatchSelectWidth={false} allowClear showSearch placeholder="选择涨粉方式" disabled={!isFieldEditable('growth_method')}
                options={withTitle(growthMethods.map(g => ({ label: g.name, value: g.name })))}
                onChange={handleGrowthMethodChange}
              />
            </Form.Item>}
            {isFieldVisible('filter_use') && <Form.Item name="filter_use" label="筛选使用" style={formItemStyle}>
              <Input placeholder="输入筛选条件" disabled={!isFieldEditable('filter_use')} />
            </Form.Item>}
          </div>
        </Card>

        {/* 链接信息 */}
        <Card title="链接信息" style={{ marginBottom: 12 }} size="small">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {isFieldVisible('link_type') && <Form.Item name="link_type" label="链接属性" style={formItemStyle}>
              <Input placeholder="输入链接属性" disabled={!isFieldEditable('link_type')} />
            </Form.Item>}
            {isFieldVisible('exclusive_link') && <Form.Item name="exclusive_link" label="专属链接/涨粉链接" style={formItemStyle}>
              <Input placeholder="输入链接" disabled={!isFieldEditable('exclusive_link')} />
            </Form.Item>}
            {isFieldVisible('account_duplicate') && <Form.Item name="account_duplicate" label="账号重复" style={formItemStyle}>
              <Input placeholder="输入账号重复信息" disabled={!isFieldEditable('account_duplicate')} />
            </Form.Item>}
            {isFieldVisible('method_duplicate') && <Form.Item name="method_duplicate" label="方式+账号重复" style={formItemStyle}>
              <Input placeholder="输入重复信息" disabled={!isFieldEditable('method_duplicate')} />
            </Form.Item>}
          </div>
        </Card>

        {/* 粉丝数据 */}
        <Card title="粉丝数据" style={{ marginBottom: 12 }} size="small">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {isFieldVisible('initial_fans') && <Form.Item name="initial_fans" label="初始粉丝数" style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} min={0} placeholder="输入粉丝数" disabled={!isFieldEditable('initial_fans')} />
            </Form.Item>}
            {isFieldVisible('final_fans') && <Form.Item name="final_fans" label="涨粉后粉丝数" style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} min={0} placeholder="输入粉丝数" disabled={!isFieldEditable('final_fans')} />
            </Form.Item>}
            {isFieldVisible('effective_fans') && <Form.Item name="effective_fans" label="最终有效粉" style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} min={0} placeholder="0" disabled={!isFieldEditable('effective_fans')} />
            </Form.Item>}
            {isFieldVisible('growth_count') && <Form.Item name="growth_count" label="涨粉量" style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} disabled placeholder="自动计算" />
            </Form.Item>}
          </div>
        </Card>

        {/* 完结信息 */}
        <Card title="完结信息" style={{ marginBottom: 12 }} size="small">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {isFieldVisible('status_id') && <Form.Item name="status_id" label="完结状态" style={formItemStyle}>
              <Select allowClear placeholder="选择状态" disabled={!isFieldEditable('status_id')}
                popupMatchSelectWidth={false}
               
                options={withTitle(selectableStatuses.map(s => ({
                  label: s.name,
                  value: s.id
                })))}
              />
            </Form.Item>}
            {isFieldVisible('complete_date') && <Form.Item name="complete_date" label="完成日期" style={formItemStyle}>
              <DatePicker style={{ width: '100%' }} disabled={!isFieldEditable('complete_date')} />
            </Form.Item>}
            {isFieldVisible('text3') && <Form.Item name="text3" label="文本3" style={formItemStyle}>
              <Input placeholder="输入文本" disabled={!isFieldEditable('text3')} />
            </Form.Item>}
            {isFieldVisible('pre_growth_text') && <Form.Item name="pre_growth_text" label="涨粉前截图文字" style={formItemStyle}>
              <TextArea rows={1} placeholder="输入截图文字" disabled={!isFieldEditable('pre_growth_text')} />
            </Form.Item>}
            {isFieldVisible('post_growth_text') && <Form.Item name="post_growth_text" label="涨粉完结截图文字" style={formItemStyle}>
              <TextArea rows={1} placeholder="输入截图文字" disabled={!isFieldEditable('post_growth_text')} />
            </Form.Item>}
            {isFieldVisible('remark') && <Form.Item name="remark" label="备注" style={formItemStyle}>
              <TextArea rows={1} placeholder="输入备注" disabled={!isFieldEditable('remark')} />
            </Form.Item>}
          </div>
        </Card>

        {/* 价格信息 */}
        <Card title="价格信息" style={{ marginBottom: 12 }} size="small">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {isFieldVisible('reference_price') && <Form.Item name="reference_price" label="参考价格" style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} min={0} precision={2} prefix="¥" placeholder="输入价格" disabled={!isFieldEditable('reference_price')} />
            </Form.Item>}
            {isFieldVisible('actual_price') && <Form.Item name="actual_price" label="实际价格" style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} min={0} precision={2} prefix="¥" placeholder="输入价格" disabled={!isFieldEditable('actual_price')} />
            </Form.Item>}
            {isFieldVisible('text1') && <Form.Item name="text1" label="文本1" style={formItemStyle}>
              <Input placeholder="输入文本" disabled={!isFieldEditable('text1')} />
            </Form.Item>}
            {isFieldVisible('text2') && <Form.Item name="text2" label="文本2" style={formItemStyle}>
              <Input placeholder="输入文本" disabled={!isFieldEditable('text2')} />
            </Form.Item>}
          </div>
        </Card>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Space>
            {(!isNew || canAdd) && (
              <Button type="primary" loading={saving} size="large" onClick={handleSave}>
                {isNew ? '批量创建订单' : '保存修改'}
              </Button>
            )}
            <Button size="large" onClick={() => navigate('/orders')}>
              返回列表
            </Button>
          </Space>
        </div>
      </Form>

      {/* 查重弹窗 */}
      <Modal
        title="查重结果"
        open={dupVisible}
        onCancel={() => { setDupVisible(false); setDupData([]); setPendingValues(null); setPendingAccounts([]); }}
        footer={[
          <Button key="cancel" onClick={() => { setDupVisible(false); setDupData([]); setPendingValues(null); setPendingAccounts([]); }}>取消</Button>,
          <Button key="dedup" onClick={handleDedupCreate} loading={saving}>去除重复后添加</Button>,
          <Button key="force" type="primary" onClick={handleForceCreate} loading={saving}>全部添加（含重复）</Button>
        ]}
        width={600}
      >
        {dupData.length === 0 ? (
          <p style={{ color: '#52c41a' }}>未发现重复数据</p>
        ) : (
          <div>
            <p style={{ color: '#ff4d4f', marginBottom: 12 }}>发现以下重复的抖音号：</p>
            <Table
              size="small"
              pagination={false}
              dataSource={dupData.map((d, i) => ({ ...d, _key: i }))}
              rowKey="_key"
              columns={[
                { title: '抖音号', dataIndex: 'account_name' },
                { title: '涨粉方式', dataIndex: 'growth_method' },
                { title: '主体', dataIndex: 'supplier_name', render: v => v || '-' },
                { title: '小组', dataIndex: 'team', render: v => v || '-' },
                { title: '已存在订单ID', dataIndex: 'id', width: 100 }
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default OrderDetail;
